import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import MarketingHeader from "@/components/MarketingHeader";
import MarketingFooter from "@/components/MarketingFooter";
import { verifyResetCode, completePasswordReset } from "@/integrations/backend/auth";
import frequentHealthIcon from "@/assets/frequent-health-icon.jpeg";

export default function ResetPasswordConfirm() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"loading" | "form" | "success" | "error">("loading");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [code, setCode] = useState<string>("");
  
  // Form state
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const codeParam = searchParams.get("code");
    
    if (!codeParam) {
      setStatus("error");
      setErrorMessage("Invalid reset link. No code provided.");
      return;
    }

    setCode(codeParam);

    const verify = async () => {
      try {
        await verifyResetCode(codeParam);
        setStatus("form");
        toast.success("Reset code verified. Please enter your new password.");
      } catch (error: any) {
        setStatus("error");
        setErrorMessage(error.message || "Invalid or expired reset code. Please request a new one.");
        toast.error(error.message || "Verification failed");
      }
    };

    verify();
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!password || !confirmPassword) {
      toast.error("Please fill in all fields");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (password.length < 8) {
      toast.error("Password must be at least 8 characters long");
      return;
    }

    setIsSubmitting(true);

    try {
      await completePasswordReset(code, password);
      setStatus("success");
      toast.success("Password reset successfully!");
      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate("/login", { replace: true });
      }, 2000);
    } catch (error: any) {
      setErrorMessage(error.message || "Failed to reset password. Please try again.");
      toast.error(error.message || "Reset failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <MarketingHeader />
      <div className="min-h-screen gradient-dark flex items-center justify-center p-4 pt-24">
        <Card className="w-full max-w-md border-primary/20 shadow-neon-glow bg-dark-card">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <img
                src={frequentHealthIcon}
                alt="Frequent Health logo"
                className="h-12 w-auto"
              />
            </div>
            <CardTitle className="text-2xl text-white">
              {status === "loading" && "Verifying reset code..."}
              {status === "form" && "Reset Your Password"}
              {status === "success" && "Password Reset Complete!"}
              {status === "error" && "Reset Failed"}
            </CardTitle>
            <p className="text-gray-light">
              {status === "loading" && "Please wait while we verify your reset code..."}
              {status === "form" && "Enter your new password below"}
              {status === "success" && "Your password has been reset! Redirecting to login..."}
              {status === "error" && errorMessage}
            </p>
          </CardHeader>
          <CardContent>
            {status === "loading" && (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            )}
            
            {status === "form" && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-gray-light">New Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isSubmitting}
                    minLength={8}
                    className="bg-dark-slate border-primary/20 text-white placeholder:text-gray-mid"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-password" className="text-gray-light">Confirm Password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    disabled={isSubmitting}
                    minLength={8}
                    className="bg-dark-slate border-primary/20 text-white placeholder:text-gray-mid"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full font-bold uppercase tracking-wider shadow-neon-glow hover:scale-105 transition-all"
                >
                  {isSubmitting ? "Resetting Password..." : "Reset Password"}
                </Button>
              </form>
            )}

            {status === "success" && (
              <div className="text-center py-4">
                <div className="text-4xl mb-4">✓</div>
                <p className="text-gray-light">You will be redirected to the login page shortly.</p>
              </div>
            )}

            {status === "error" && (
              <div className="text-center py-4">
                <div className="text-4xl mb-4 text-red-500">✗</div>
                <p className="text-gray-light mb-4">{errorMessage}</p>
                <div className="space-y-2">
                  <button
                    onClick={() => navigate("/reset-password")}
                    className="text-primary hover:text-lime-hover font-medium block w-full"
                  >
                    Request New Reset Link
                  </button>
                  <button
                    onClick={() => navigate("/login")}
                    className="text-gray-light hover:text-white text-sm block w-full"
                  >
                    Back to Login
                  </button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      <MarketingFooter />
    </>
  );
}

